import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Box,
  Typography,
  Avatar,
  Collapse
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Paid as PaidIcon,
  Receipt as ReceiptIcon,
  Assessment as AssessmentIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  PointOfSale as PosIcon,
  CalendarMonth as CalendarMonthIcon,
  CalendarToday as CalendarTodayIcon,
  DateRange as DateRangeIcon,
  People as PeopleIcon,
  AccountBalanceWallet as WalletIcon,
  AttachMoney as MoneyIcon,
  PieChart as ChartIcon,
  MonetizationOn as MonetizationOnIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  EventNote as EventNoteIcon,
  Payments as PaymentsIcon,
  AccountBalance as AccountBalanceIcon,
  Description as DescriptionIcon,
  Inventory as InventoryIcon,
  ShoppingCart as ShoppingCartIcon,
  CompareArrows as CompareArrowsIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

interface AppDrawerProps {
  onClose?: () => void;
}

const AppDrawer: React.FC<AppDrawerProps> = ({ onClose }) => {
  const { currentUser, userData, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // State for collapsible menu items
  const [closingOpen, setClosingOpen] = useState(false);
  const [earningsOpen, setEarningsOpen] = useState(false);
  const [inventoryOpen, setInventoryOpen] = useState(false);

  // Check if any child routes are active to auto-expand parent menu
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/daily-closing') || path.includes('/monthly-closing')) {
      setClosingOpen(true);
    }
    if (path.includes('/commission-breakdown') || path.includes('/sales-split-breakdown')) {
      setEarningsOpen(true);
    }
    if (path.includes('/inventory') || path.includes('/purchase-receipts') || path.includes('/inventory-adjustments')) {
      setInventoryOpen(true);
    }
  }, [location.pathname]);

  const handleNavigation = (path: string) => {
    navigate(path);
    if (onClose) {
      onClose();
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Expenses', icon: <WalletIcon />, path: '/expenses' },
    { text: 'Income', icon: <MoneyIcon />, path: '/income' },
    { text: 'GL Accounts', icon: <AccountBalanceIcon />, path: '/gl-accounts' },
    { text: 'Journal Entry', icon: <DescriptionIcon />, path: '/journal' },
    { text: 'Reports', icon: <ChartIcon />, path: '/reports' },
    { text: 'Invoices', icon: <ReceiptIcon />, path: '/invoices' }
  ];
  
  // Nested menu items
  const closingItems = [
    { text: 'Daily Closing', icon: <PosIcon />, path: '/daily-closing' },
    { text: 'Monthly Closing', icon: <CalendarMonthIcon />, path: '/monthly-closing' }
  ];
  
  const earningsItems = [
    { text: 'Commission Breakdown', icon: <PeopleIcon />, path: '/commission-breakdown' },
    { text: 'Sales Split Breakdown', icon: <MonetizationOnIcon />, path: '/sales-split-breakdown' }
  ];
  
  // Inventory related items
  const inventoryItems = [
    { text: 'Inventory Dashboard', icon: <InventoryIcon />, path: '/inventory' },
    { text: 'Purchase Receipts', icon: <ShoppingCartIcon />, path: '/purchase-receipts' },
    { text: 'Inventory Adjustments', icon: <CompareArrowsIcon />, path: '/inventory-adjustments' },
    { text: 'Inventory COGS', icon: <MonetizationOnIcon />, path: '/inventory-cogs' }
  ];

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        p: 2
      }}>
        <Avatar 
          sx={{ 
            width: 64, 
            height: 64, 
            mb: 1,
            bgcolor: 'primary.main',
            fontSize: '1.5rem'
          }}
        >
          {currentUser?.displayName?.[0]?.toUpperCase() || currentUser?.email?.[0]?.toUpperCase() || 'F'}
        </Avatar>
        <Typography variant="subtitle1" fontWeight={600}>
          {userData?.displayName || currentUser?.displayName || currentUser?.email}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {userData?.role || 'Finance Administrator'}
        </Typography>
      </Box>
      <Divider />
      <List>
        {/* Regular menu items */}
        {menuItems.map((item) => (
          <ListItem 
            key={item.text} 
            disablePadding
            sx={{
              borderLeft: location.pathname === item.path ? '4px solid' : '4px solid transparent',
              borderLeftColor: 'primary.main'
            }}
          >
            <ListItemButton onClick={() => handleNavigation(item.path)} sx={{ pl: 2 }}>
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
        
        {/* Closing group */}
        <ListItem disablePadding>
          <ListItemButton onClick={() => setClosingOpen(!closingOpen)} sx={{ pl: 2 }}>
            <ListItemIcon>
              <EventNoteIcon />
            </ListItemIcon>
            <ListItemText primary="Closing" />
            {closingOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </ListItemButton>
        </ListItem>
        <Collapse in={closingOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {closingItems.map((item) => (
              <ListItem 
                key={item.text} 
                disablePadding
                sx={{
                  borderLeft: location.pathname === item.path ? '4px solid' : '4px solid transparent',
                  borderLeftColor: 'primary.main'
                }}
              >
                <ListItemButton onClick={() => handleNavigation(item.path)} sx={{ pl: 4 }}>
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Collapse>
        
        {/* Inventory group */}
        <ListItem disablePadding>
          <ListItemButton onClick={() => setInventoryOpen(!inventoryOpen)} sx={{ pl: 2 }}>
            <ListItemIcon>
              <InventoryIcon />
            </ListItemIcon>
            <ListItemText primary="Inventory" />
            {inventoryOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </ListItemButton>
        </ListItem>
        <Collapse in={inventoryOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {inventoryItems.map((item) => (
              <ListItem 
                key={item.text} 
                disablePadding
                sx={{
                  borderLeft: location.pathname === item.path ? '4px solid' : '4px solid transparent',
                  borderLeftColor: 'primary.main'
                }}
              >
                <ListItemButton onClick={() => handleNavigation(item.path)} sx={{ pl: 4 }}>
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Collapse>
        
        {/* Staff Earnings group */}
        <ListItem disablePadding>
          <ListItemButton onClick={() => setEarningsOpen(!earningsOpen)} sx={{ pl: 2 }}>
            <ListItemIcon>
              <PaymentsIcon />
            </ListItemIcon>
            <ListItemText primary="Staff Earnings" />
            {earningsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </ListItemButton>
        </ListItem>
        <Collapse in={earningsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {earningsItems.map((item) => (
              <ListItem 
                key={item.text} 
                disablePadding
                sx={{
                  borderLeft: location.pathname === item.path ? '4px solid' : '4px solid transparent',
                  borderLeftColor: 'primary.main'
                }}
              >
                <ListItemButton onClick={() => handleNavigation(item.path)} sx={{ pl: 4 }}>
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Collapse>
      </List>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={() => handleNavigation('/settings')}>
            <ListItemIcon><SettingsIcon /></ListItemIcon>
            <ListItemText primary="Settings" />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout}>
            <ListItemIcon><LogoutIcon /></ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );
};

export default AppDrawer;
